import { useCallback, useMemo } from 'react';
import {
  Radio,
  TableCellTypes,
  Badge,
  BadgeSizes,
  BadgeVariants,
  Button,
  ButtonSizes,
  ButtonVariants,
  Dropdown,
  TextInput,
  InputStatuses,
  Typography,
  TypographyVariants,
  TypographyElements,
  TypographyColors,
  TypographyWeights,
} from '@carboledger/ui';
import { useTranslations, useRowUnitType } from '@/hooks';
import { rowUnitSymbol } from '@/lib/mappers/quantities';
import { discrepancyLabel } from '@/config/discrepancies';
import styles from './tableCells.module.scss';

// Cell renderers shared by every list table.

// `weight` omitted → Typography keeps the variant's own weight.
export const CellText = ({
  children,
  color = TypographyColors.primary,
  weight,
  truncate = false,
}) => (
  <Typography
    as={TypographyElements.span}
    variant={TypographyVariants.bodySm}
    color={color}
    weight={weight}
    truncate={truncate}
  >
    {children}
  </Typography>
);

/**
 * Props:
 *   row     the row this cell belongs to — passed back to onOpen
 *   value   the id text to render
 *   onOpen  (row) => void — makes the text a button; omit for plain text
 */
export const CellLink = ({ row, value, onOpen }) => {
  const handleClick = useCallback(() => onOpen?.(row), [onOpen, row]);

  // Nothing to open, so no button — an empty one is just a keyboard trap.
  if (!value || !onOpen) return <CellText truncate>{value}</CellText>;

  return (
    <button type="button" className={styles.linkTrigger} onClick={handleClick}>
      <CellText truncate weight={TypographyWeights.semibold}>
        {value}
      </CellText>
    </button>
  );
};

/**
 * A 1-indexed row number. `offset` continues the count across pages — pass
 * `(page - 1) * pageSize`; omit it for an unpaginated table.
 */
export const makeSerialCell = (offset = 0) => {
  const renderSerial = (ctx) => <CellText>{offset + ctx.rowIndex + 1}</CellText>;
  return renderSerial;
};

/** CellLink with a click handler bound; `onOpen` may be undefined. */
export const makeLinkCell = (onOpen) => {
  const renderCell = (ctx) => <CellLink row={ctx.row} value={ctx.value} onOpen={onOpen} />;
  return renderCell;
};

/**
 * The unit-type cell — the badge IS the dropdown and switches this row only; the
 * column's headerSelect switches every row.
 *
 * @param getOptions (t, row) => Option[] — the slice's unit options for that row
 */
export const makeUnitTypeCell = (getOptions) => {
  const UnitTypeCell = ({ row }) => {
    const t = useTranslations();
    const { unitType, setUnitType } = useRowUnitType(row.id, row.unitType);
    const handleChange = useCallback(
      (event) => setUnitType?.(row.id, event.target.value),
      [setUnitType, row.id],
    );
    const options = useMemo(() => getOptions(t, row), [t, row]);

    // Nothing to switch to, or the column's chosen unit isn't one this row has.
    const hasSelected = options.some((option) => option.value === unitType);
    if (!options.length || !hasSelected)
      return <CellText color={TypographyColors.secondary}>-</CellText>;

    return (
      <Dropdown
        appearance="badge"
        badgeVariant={BadgeVariants.default}
        value={unitType}
        onChange={handleChange}
        options={options}
        aria-label={t('UNIT_TYPE_ARIA')}
      />
    );
  };
  const renderUnitTypeCell = (ctx) => <UnitTypeCell row={ctx.row} />;
  return renderUnitTypeCell;
};

/** A quantity column: reads the slice of `byUnit` the row is currently showing. */
export const makeUnitScopedCell = (field) => {
  const UnitScopedCell = ({ row }) => {
    const { unitType } = useRowUnitType(row.id, row.unitType);
    // No fallback to another unit — showing one unit's number under another's label lies.
    const view = row.byUnit?.[unitType];
    return <CellText>{view?.[field] || '-'}</CellText>;
  };
  const renderUnitScopedCell = (ctx) => <UnitScopedCell row={ctx.row} />;
  return renderUnitScopedCell;
};

/**
 * Props:
 *   row       the row this cell belongs to
 *   value     the quantity typed so far
 *   onChange  (rowId, quantity) => void
 */
const AssignQuantityCell = ({ row, value = '', onChange, overCapKey }) => {
  const t = useTranslations();
  const { unitType } = useRowUnitType(row.id, row.unitType);
  const unit = rowUnitSymbol(row, unitType);
  const cap = row.unassignedByUnit?.[unitType] ?? null;

  const amount = Number(value);
  const invalid = value !== '' && (!Number.isFinite(amount) || amount <= 0);
  const overCap = !invalid && value !== '' && cap != null && amount > cap;
  const message = overCap
    ? t(overCapKey ?? 'LINK_ASSIGN_OVER_AVAILABLE')
    : invalid
      ? t('VALIDATION_POSITIVE_NUMBER')
      : null;

  const handleChange = useCallback(
    (event) => onChange(row.id, event.target.value),
    [onChange, row.id],
  );

  return (
    <TextInput
      value={value}
      onChange={handleChange}
      placeholder={t('LINK_ASSIGN_QUANTITY')}
      suffix={unit || undefined}
      status={message ? InputStatuses.error : undefined}
      message={message}
      aria-label={t('LINK_ASSIGN_QUANTITY')}
    />
  );
};

/** AssignQuantityCell bound to the wizard's `assignments` map; `overCapKey` overrides its error. */
export const makeAssignQuantityCell = (assignments, onAssign, overCapKey) => {
  const renderAssign = (ctx) => (
    <AssignQuantityCell
      row={ctx.row}
      value={assignments[ctx.row.id]}
      onChange={onAssign}
      overCapKey={overCapKey}
    />
  );
  return renderAssign;
};

/**
 * Props:
 *   row      the row this cell belongs to — passed back to onRemove
 *   onRemove (rowId) => void — drops the row from step 2
 */
export const RemoveCell = ({ row, onRemove }) => {
  const t = useTranslations();
  const handleClick = useCallback(() => onRemove?.(row.id), [onRemove, row.id]);
  return (
    <Button variant={ButtonVariants.tertiary} size={ButtonSizes.sm} onClick={handleClick}>
      {t('COMMON_REMOVE')}
    </Button>
  );
};

/** A link wizard's step-2 Remove action, bound to the modal's row dropper. */
export const makeRemoveCell = (onRemove) => {
  const renderRemove = (ctx) => <RemoveCell row={ctx.row} onRemove={onRemove} />;
  return renderRemove;
};

/**
 * Props:
 *   row        the row this radio belongs to
 *   name       the radio group's name, so two pickers on one screen stay separate
 *   selectedId the picked row id
 *   onSelect   (rowId) => void
 */
// TODO: name the control once the library Radio takes an aria-label.
export const PickCell = ({ row, name, selectedId, onSelect }) => {
  const handleChange = useCallback(() => onSelect?.(row.id), [onSelect, row.id]);
  return (
    <Radio
      name={name}
      value={String(row.id)}
      checked={selectedId === row.id}
      onChange={handleChange}
    />
  );
};

/** The leading radio column every single-select picker table carries. */
export const buildPickColumn = ({ name, selectedId, onSelect }) => ({
  id: '__pick__',
  header: '',
  type: TableCellTypes.custom,
  width: 56,
  resizable: false,
  reorderable: false,
  cell: (ctx) => <PickCell row={ctx.row} name={name} selectedId={selectedId} onSelect={onSelect} />,
});

// Discrepancies badge: colour escalates with the count, label caps at "3+".

const discrepancyVariant = (count) => {
  if (!count) return BadgeVariants.default;
  if (count === 1) return BadgeVariants.info;
  if (count === 2) return BadgeVariants.warning;
  return BadgeVariants.error;
};

/**
 * Props:
 *   row     the row this cell belongs to — passed back to onOpen
 *   count   this row's discrepancy count
 *   onOpen  (row) => void — makes the badge a button; omit for a static badge
 */
export const DiscrepanciesCell = ({ row, count = 0, onOpen }) => {
  const t = useTranslations();
  const handleClick = useCallback(() => onOpen?.(row), [onOpen, row]);

  const label = discrepancyLabel(t, count);

  const badge = (
    <Badge variant={discrepancyVariant(count)} size={BadgeSizes.sm}>
      {label}
    </Badge>
  );

  // Badge takes no onClick, so an interactive one is wrapped in a real button.
  if (!count || !onOpen) return badge;
  return (
    <button type="button" className={styles.discrepancyTrigger} onClick={handleClick}>
      {badge}
    </button>
  );
};

export const renderDiscrepanciesCell = (ctx) => (
  <DiscrepanciesCell row={ctx.row} count={ctx.row.discrepancyCount ?? ctx.value} />
);

/** renderDiscrepanciesCell with a click handler bound; `onOpen` may be undefined. */
export const makeDiscrepanciesCell = (onOpen) => {
  const renderCell = (ctx) => (
    <DiscrepanciesCell
      row={ctx.row}
      count={ctx.row.discrepancyCount ?? ctx.value}
      onOpen={onOpen}
    />
  );
  return renderCell;
};

/**
 * Props:
 *   row      the row the action opens
 *   onOpen   (row) => void
 */
const ViewButtonCell = ({ row, onOpen }) => {
  const t = useTranslations();
  const handleClick = useCallback(() => onOpen(row), [onOpen, row]);

  return (
    <Button variant={ButtonVariants.tertiary} size={ButtonSizes.sm} onClick={handleClick}>
      {t('COMMON_VIEW')}
    </Button>
  );
};

// A cell whose value is an action — `canOpen` dashes out a row with nothing to open.
export const makeViewBtnCell = (onOpen, canOpen) => {
  const renderCell = (ctx) =>
    !canOpen || canOpen(ctx.row) ? (
      <ViewButtonCell row={ctx.row} onOpen={onOpen} />
    ) : (
      <CellText color={TypographyColors.secondary}>-</CellText>
    );
  return renderCell;
};
